import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Autocomplete, Box, TextField } from '@mui/material';

import api from '../../api';
import { NEXWAY } from '../../constants/consts';
import { setSelectedCustomer } from '../../redux/actions/Account';
import { nexwayStateSelector } from '../../redux/selectors/Account';

import '../../styles/main.scss';

const CustomerHandling = () => {
  const dispatch = useDispatch();
  const isInitialized = useRef(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [accountCustomer, setAccountCustomer] = useState({});
  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const account = useSelector(({ account: { user } }) => user);
  const accountId = account?.customerId;
  const { selectedCustomer, recentCustomers } = useSelector(nexwayStateSelector);

  const fetchAllCustomers = useCallback(async () => {
    if (allCustomers.length > 0) {
      return allCustomers;
    }

    setLoading(true);

    try {
      const allCustomersData = await api.getAllCustomersByName('');
      const items = allCustomersData.items || [];
      setAllCustomers(items);

      return items;
    } catch (error) {
      console.error('Error fetching customers:', error);

      return [];
    } finally {
      setLoading(false);
    }
  }, [allCustomers.length]);

  const filteredCustomers = useMemo(() => {
    if (!search?.trim()) return allCustomers;

    const lowerSearch = search.toLowerCase();

    return allCustomers.filter((customer) => customer.name?.toLowerCase().includes(lowerSearch));
  }, [search, allCustomers]);

  useEffect(() => {
    fetchAllCustomers().then((items) => {
      const accountCustomerData = items?.find((i) => i?.id === accountId);

      if (accountCustomerData) {
        setAccountCustomer({
          id: accountCustomerData?.id,
          name: accountCustomerData?.name,
          group_type: 'Account',
        });

        if (!isInitialized.current && !selectedCustomer?.id && accountCustomerData?.id !== NEXWAY) {
          isInitialized.current = true;
          dispatch(
            setSelectedCustomer({
              ...accountCustomerData,
              group_type: 'Current',
            }),
          );
        }
      }
    });
  }, [accountId, dispatch, fetchAllCustomers, selectedCustomer?.id]);

  const options = useMemo(() => {
    const opt = [];

    accountCustomer?.id && opt.push(accountCustomer);
    selectedCustomer?.id &&
      selectedCustomer?.id !== accountCustomer.id &&
      opt.push(selectedCustomer);
    recentCustomers?.length && opt.push(...recentCustomers);

    const customersToShow = filteredCustomers.length > 0 ? filteredCustomers : allCustomers;
    customersToShow?.length && opt.push(...customersToShow);

    return opt;
  }, [accountCustomer, allCustomers, filteredCustomers, recentCustomers, selectedCustomer]);

  const eraseSearch = () => {
    setSearch(null);
  };

  const handleChangeCustomer = (e, selected) => {
    e.persist();

    eraseSearch();
    dispatch(
      setSelectedCustomer(
        selected
          ? {
              ...selected,
              group_type: 'Current',
            }
          : selected,
      ),
    );
  };

  const handleInputChange = (e) => {
    e.persist();
    setSearch(e.target.value);
  };

  const handleEqual = (option) => option.id === selectedCustomer?.id;
  const handleFilterOptions = (opts) => opts;

  return (
    allCustomers && (
      <Box
        width='100%'
        m='auto'
        p='0 3px'
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        className='customers-handling'
      >
        <Autocomplete
          id='customers-select'
          loading={loading && allCustomers.length === 0}
          options={options}
          inputValue={search ?? (selectedCustomer?.name || '')}
          value={selectedCustomer?.id || null}
          onBlur={eraseSearch}
          isOptionEqualToValue={handleEqual}
          onChange={handleChangeCustomer}
          filterOptions={handleFilterOptions}
          groupBy={(option) => option.group_type || 'Search'}
          getOptionLabel={(option) => option.name || ''}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={selectedCustomer?.id ? 'Customer' : 'All Customers'}
              onChange={handleInputChange}
              name='customer-search'
              placeholder='Search...'
              variant='outlined'
            />
          )}
        />
      </Box>
    )
  );
};

export default React.memo(CustomerHandling);
